import { TimeRange } from 'features/systemLens'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import {
  BaseSensorEntry,
  ChartTimeUnit,
  DateConfig,
  SourceConfig,
  TimeSeriesDerivedValueEntryJSON,
  YAxisMode,
} from 'types'

const buildSourceConfig = (
  id: string,
  type: 'sensor' | 'derivedValue',
  colorIndex: number,
  customSensorColors: Record<string, string>,
  customAxisRanges: Record<string, { min?: number; max?: number }>,
  sensorVisibilityConfig: Record<string, boolean>,
  autoScaleMode: boolean
): SourceConfig => {
  let yAxisRange = null
  if (!autoScaleMode) {
    const axisRange = customAxisRanges[id]
    yAxisRange =
      axisRange && axisRange.min !== undefined && axisRange.max !== undefined
        ? { min: axisRange.min, max: axisRange.max }
        : null
  }

  return {
    type,
    sourceId: id,
    color: customSensorColors[id] || getSensorColor(colorIndex),
    hidden: sensorVisibilityConfig[id] === false,
    yAxisRange,
  }
}

export const buildSourceConfigsFromState = (
  selectedSensors: BaseSensorEntry[],
  customSensorColors: Record<string, string>,
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  customAxisRanges: Record<string, { min?: number; max?: number }>,
  sensorVisibilityConfig: Record<string, boolean>,
  autoScaleMode: boolean
): SourceConfig[] => {
  const sensorConfigs: SourceConfig[] = selectedSensors.map((se, sensorIndex) =>
    buildSourceConfig(
      se.id,
      'sensor',
      sensorIndex,
      customSensorColors,
      customAxisRanges,
      sensorVisibilityConfig,
      autoScaleMode
    )
  )

  const derivedConfigs: SourceConfig[] = derivedValues.map((dv, derivedIndex) =>
    buildSourceConfig(
      dv.id,
      'derivedValue',
      derivedIndex,
      customSensorColors,
      customAxisRanges,
      sensorVisibilityConfig,
      autoScaleMode
    )
  )

  return [...sensorConfigs, ...derivedConfigs]
}

export const buildYAxisModeFromState = (singleScaleMode: boolean): YAxisMode =>
  singleScaleMode ? 'single' : 'multiple'

export const buildDateConfigFromState = (
  dateMode: 'absolute' | 'relative',
  timeRange: TimeRange,
  relativeStart?: { value?: number; unit: ChartTimeUnit },
  relativeEnd?: { isNow: boolean; value?: number; unit: ChartTimeUnit },
  cadenceSeconds: number = 10
): DateConfig => {
  if (dateMode === 'absolute') {
    return {
      mode: 'absolute',
      startDate: new Date(timeRange.from),
      endDate: new Date(timeRange.to),
    }
  }
  const start = {
    value: relativeStart?.value ?? 7,
    unit: relativeStart?.unit ?? 'days',
  }
  const end =
    !relativeEnd?.isNow && relativeEnd?.value !== undefined
      ? { value: relativeEnd.value!, unit: relativeEnd.unit ?? 'days' }
      : null

  return {
    mode: 'relative',
    startDateLookback: start,
    endDateLookback: end,
    updateCadenceSeconds: cadenceSeconds,
  }
}
